import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Container,
  Typography,
  TextField,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Tooltip,
  CircularProgress
} from '@material-ui/core';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { API, graphqlOperation, Storage, Auth } from 'aws-amplify';
import { nanoid } from 'nanoid';
import { useParams } from 'react-router-dom';
import PrivacyPolicyCustomer from '../../components/privacyPolicyCustomer';
import { getRecaptchaToken, initializeRecaptcha } from '../../utils/recaptcha';

// stripePromise変数を初期状態としてnullに設定
let stripePromise = null;

// URL解析関数を追加
const parseURL = (jsonUrl) => {
  let arrayUrls = null;
  try {
    arrayUrls = JSON.parse(jsonUrl);
  } catch (e) {
    arrayUrls = jsonUrl;
  } finally {
    const typeUrls = typeof arrayUrls;
    if (typeUrls !== "string") {
      return arrayUrls;
    } else {
      return [arrayUrls];
    }
  }
};

// URLからS3のキーを抽出する関数
const extractS3KeyFromUrl = (url) => {
  if (!url) return null;
  
  // 既にキーの形式（http://から始まらない）ならそのまま返す
  if (!url.startsWith('http')) return url;
  
  try {
    // URLからimage-product/以降のパスを抽出
    const match = url.match(/\/image-product\/[^?]+/);
    if (match) {
      return decodeURIComponent(match[0].substring(1)); // 先頭の / を削除
    }
    return null;
  } catch (error) {
    console.error("URLからキーの抽出に失敗:", error);
    return null;
  }
};

// PurchaseProductWrapperコンポーネントを修正
const PurchaseProductWrapper = () => {
  const { UUID } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initStripe = async () => {
      try {
        // 商品情報を取得してStripeを初期化
        const getProductQuery = `
          query GetProduct($id: ID!) {
            getProduct(id: $id) {
              id
              stripeID
            }
          }
        `;

        const response = await API.graphql({
          ...graphqlOperation(getProductQuery, { id: UUID }),
          authMode: 'API_KEY'
        });

        const product = response.data.getProduct;
        
        if (!product || !product.stripeID) {
          console.error('商品情報またはStripe IDが取得できませんでした');
          setError('商品情報の読み込みに失敗しました');
          setIsLoading(false);
          return;
        }

        // この部分が重要: stripeAccountパラメータを追加
        stripePromise = loadStripe('pk_live_DC1kWkRom2sPDMx3O77WnDKK004kECwG7T', {
          stripeAccount: product.stripeID
        });
        
        setIsLoading(false);
      } catch (error) {
        console.error('Stripe初期化エラー:', error);
        setError('決済システムの初期化に失敗しました');
        setIsLoading(false);
      }
    };

    initStripe();
  }, [UUID]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <Typography color="error">{error}</Typography>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <PurchaseProduct />
    </Elements>
  );
};

const PurchaseProduct = () => {
  const { UUID } = useParams(); // URLのパラメータ名と一致させる
  const [overlay, setOverlay] = useState(false);
  const [product, setProduct] = useState({});
  const [price, setPrice] = useState(0);
  const [rawPrice, setRawPrice] = useState('');
  const [showImg, setShowImg] = useState(false);
  const [imgURL, setImgURL] = useState(null);
  const [showInput, setShowInput] = useState(true);
  const [showPrice, setShowPrice] = useState(true);
  const [showThxPage, setShowThxPage] = useState(false);
  const [showStopFlag, setShowStopFlag] = useState(false);
  const [familyName, setFamilyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [URL, setURL] = useState([]);
  const [userID] = useState(nanoid());
  const [checkPrivacyPolicy, setCheckPrivacyPolicy] = useState(false);
  const [checkProvideInfo, setCheckProvideInfo] = useState(false);
  const [cvcModal, setCvcModal] = useState(false);
  const [token, setToken] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const formRef = useRef();
  const stripe = useStripe();
  const elements = useElements();
  const [cardElement, setCardElement] = useState(null);
  const [cvcDialogOpen, setCvcDialogOpen] = useState(false);

  // カードスタイルの定義
  const cardStyle = {
    base: {
      color: "#32325d",
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4"
      }
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a"
    }
  };

  useEffect(() => {
    if (!stripe || !elements || !product.stripeID) return;
  
    const setupStripe = () => {
      try {
        const element = elements.create("card", {
          style: cardStyle,
          hidePostalCode: true,
        });
        element.mount("#card-element");
        setCardElement(element);
      } catch (error) {
        console.error('Stripe setup error:', error);
      }
    };
  
    setupStripe();
  
    return () => {
      if (cardElement) {
        cardElement.unmount();
      }
    };
  }, [stripe, elements, product.stripeID]);

  // reCAPTCHAトークンを取得
  useEffect(() => {
    const getToken = async () => {
      try {
        const captchaToken = await initializeRecaptcha('charge');
        if (captchaToken) {
          setToken(captchaToken);
        } else {
          console.warn('reCAPTCHAトークンが取得できませんでした');
        }
      } catch (error) {
        console.error('reCAPTCHA初期化エラー:', error);
      }
    };
    
    getToken();
    
    // 60秒ごとにトークンを更新
    const intervalId = setInterval(async () => {
      try {
        const newToken = await getRecaptchaToken('charge');
        if (newToken) {
          setToken(newToken);
        }
      } catch (error) {
        console.error('reCAPTCHAトークン更新エラー:', error);
      }
    }, 60000);
    
    return () => clearInterval(intervalId);
  }, []);

  // バリデーションルール
  const required = (value) => !!value || '必須事項です';
  const limitLength = (value) => value.length <= 100 || '100字以内で入力してください';
  const validateEmail = (value) => {
    const regEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regEmail.test(value) || 'メールアドレスを入力してください';
  };

  useEffect(() => {
    const getProduct = async () => {
      if (!UUID) {
        return;
      }

      try {
        const getProductQuery = `
          query GetProduct($id: ID!) {
            getProduct(id: $id) {
              id
              title
              description
              price
              note
              imgURL
              stopFlag
              disableFlag
              stripeID
              sellerID
              identityId
            }
          }
        `;

        const response = await API.graphql({
          ...graphqlOperation(getProductQuery, { id: UUID }),
          authMode: 'API_KEY'
        });

        const productData = response.data.getProduct;
        if (!productData) {
          console.error('商品データが取得できませんでした');
          return;
        }

        setProduct(productData);
        setPrice(productData.price);
        setRawPrice(productData.price.toLocaleString());

      // 商品の状態チェック
      if (!productData.stopFlag && !productData.disableFlag) {
        await createPaymentIntent(productData);
      } else {
        setShowStopFlag(true);
      }

      // 画像の確認（修正版）
      if (
        productData.imgURL &&
        typeof productData.imgURL === "string" &&
        !["", "null", "undefined"].includes(productData.imgURL.trim())
      ) {
        const trimmed = productData.imgURL.trim();
        try {
          const s3Key = extractS3KeyFromUrl(trimmed);
          
          if (s3Key) {
            try {
              const url = await Storage.get(s3Key, {
                level: "public",
                expires: 86400, // 24時間の有効期限
                validateObjectExistence: true,
                identityId: productData.identityId // 商品所有者のidentityIdを使用
              });
              setImgURL(url);
              setShowImg(true);
            } catch (storageErr) {
              // エラーが発生した場合、publicなURLを直接構築
              const publicUrl = `https://amplify-pittaputreact1-newdev-5a5ac-deployment.s3.ap-northeast-1.amazonaws.com/public/${s3Key}`;
              setImgURL(publicUrl);
              setShowImg(true);
              console.warn("S3からの画像取得に失敗しましたが、public URLを使用します:", storageErr.message);
            }
          } else {
            setImgURL(trimmed);
            setShowImg(true);
          }
        } catch (err) {
          console.error("画像の処理に失敗しました:", err);
          setShowImg(false);
        }
      } else {
        setShowImg(false);
      }
      } catch (error) {
        console.error('商品取得エラー:', error);
      }
    };

    getProduct();
  }, [UUID]);

  const createPaymentIntent = async (productData) => {
    try {
      // 必要なデータの存在確認
      if (!productData || !productData.price || !productData.stripeID) {
        console.error('必要なデータが不足しています:', { 
          price: productData?.price, 
          stripeID: productData?.stripeID 
        });
        throw new Error('必要なデータが不足しています');
      }

      const mutation = `
        mutation CreatePaymentIntent($input: CreatePaymentIntentInput!) {
          createPaymentIntent(input: $input) {
            statusCode
            body
          }
        }
      `;

      const response = await API.graphql({
        ...graphqlOperation(mutation, {
          input: {
            platformFee: 200,
            price: productData.price,
            stripeID: productData.stripeID
          }
        }),
        authMode: 'API_KEY'
      });

      // レスポンスの確認
      if (!response?.data?.createPaymentIntent?.body) {
        console.error('PaymentIntentのレスポンスが不正です:', response);
        throw new Error('PaymentIntentの作成に失敗しました');
      }

      setClientSecret(response.data.createPaymentIntent.body);

    } catch (error) {
      console.error('PaymentIntent作成エラー:', error);
      // エラーハンドリング（必要に応じてUIにエラーを表示）
      setOverlay(false);  // ローディングを停止
      alert('決済の準備に失敗しました。もう一度お試しください。');
    }
  };

  const handlePayment = async () => {
    // フォームバリデーションの実装
    if (!familyName || !firstName || !email) {
      alert('必須項目を入力してください');
      return;
    }

    // メールアドレスの検証
    const regEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regEmail.test(email)) {
      alert('有効なメールアドレスを入力してください');
      return;
    }

    if (!checkPrivacyPolicy || !checkProvideInfo) {
      alert('個人情報に同意してください');
      return;
    }

    // reCAPTCHAトークンの検証
    if (!token) {
      try {
        // トークンを再取得
        const newToken = await getRecaptchaToken('charge');
        if (newToken) {
          setToken(newToken);
        } else {
          alert('セキュリティチェックに失敗しました。ページを再読み込みして再度お試しください。');
          return;
        }
      } catch (error) {
        console.error('reCAPTCHAトークン取得エラー:', error);
        alert('セキュリティチェックに失敗しました。ページを再読み込みして再度お試しください。');
        return;
      }
    }

    setOverlay(true);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement('card'),
            billing_details: {
              name: `${familyName}${firstName}`
            }
          }
        }
      );

      if (error) {
        throw new Error(error.message);
      }

      if (paymentIntent.status === 'succeeded') {
        await handleSuccessfulPayment(paymentIntent);
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('決済に失敗しました: ' + error.message);
    } finally {
      setOverlay(false);
    }
  };

  const handleSuccessfulPayment = async (paymentIntent) => {
    try {
      const mutation = `
        mutation ChargeStripe($input: ChargeStripeInput!) {
          chargeStripe(input: $input) {
            statusCode
            body
          }
        }
      `;

      const response = await API.graphql({
        ...graphqlOperation(mutation, {
          input: {
            id: userID,
            productID: product.id,
            amount: price,
            familyName,
            firstName,
            name: `${familyName}${firstName}`,
            email,
            note: note.replace(/\n/g, '\\n'),
            type: 'card',
            token: token,
            status: paymentIntent.status,
            paymentIntentID: paymentIntent.id,
            stripeID: product.stripeID,
            sellerID: product.sellerID
          }
        }),
        authMode: 'API_KEY'
      });
      
      // レスポンスの妥当性チェックを追加
      if (!response?.data?.chargeStripe?.body) {
        console.error('APIレスポンスが不正です:', response);
        throw new Error('商品URLの取得に失敗しました');
      }

      // 特定のエラー値をチェック
      const responseBody = response.data.chargeStripe.body;
      if (responseBody === 'Failed' || responseBody === 'robot!') {
        console.error('APIから特定のエラーが返されました:', responseBody);
        throw new Error('商品URLの解析に失敗しました');
      }

      const urls = parseURL(responseBody);
      setURL(Array.isArray(urls) ? urls : []);
      setShowPrice(false);
      setShowInput(false);
      setShowThxPage(true);
    } catch (error) {
      setShowPrice(false);
      setShowInput(false);
      setShowThxPage(true);
      setURL([]);
    }
  };

  return (
    <Container maxWidth="md">
      {/* Elementsを削除し、直接Containerから始める */}
      {overlay && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100%"
          height="100%"
          bgcolor="rgba(0,0,0,0.5)"
          display="flex"
          justifyContent="center"
          alignItems="center"
          zIndex={1000}
        >
          <CircularProgress />
        </Box>
      )}

      {/* 商品情報表示部分 */}
      {showPrice && (
      <Box textAlign="center" mb={4}>
        {imgURL && typeof imgURL === 'string' && !['null', 'undefined'].includes(imgURL) ? (
          <Box my={3}>
            <img 
              src={imgURL} 
              alt={product.title || '商品画像'} 
              style={{ maxWidth: "80%" }} 
            />
          </Box>
        ) : null}

        <Typography variant="h4" gutterBottom style={{ fontWeight: 'bold' }}>
          {product.title}
        </Typography>
        <Typography 
          variant="body1" 
          gutterBottom 
          style={{ textAlign: 'left', whiteSpace: 'pre-wrap' }}
        >
          {product.description}
        </Typography>
        <Typography variant="h5" gutterBottom>
          価格： {rawPrice} 円
        </Typography>
      </Box>
    )}

      {/* 入力フォーム部分 */}
      {showInput && (
        <Box component="form" noValidate autoComplete="off" ref={formRef}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="姓"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="名"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="メールアドレス"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" style={{ 
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {product.note?.split(/\r?\n/).map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="備考欄"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Grid>

            {/* プライバシーポリシーを追加 */}
            <Grid item xs={12}>
              <PrivacyPolicyCustomer />
            </Grid>
          </Grid>

          {/* チェックボックスを中央揃えに */}
          <Box display="flex" flexDirection="column" alignItems="center" my={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkPrivacyPolicy}
                  onChange={(e) => setCheckPrivacyPolicy(e.target.checked)}
                  color="primary"
                />
              }
              label="個人情報の取り扱いについて同意する"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkProvideInfo}
                  onChange={(e) => setCheckProvideInfo(e.target.checked)}
                  color="primary"
                />
              }
              label="出品者への氏名・メールアドレス提供に同意する"
            />
          </Box>

          <Box textAlign="center" my={2}>
            <Button
              color="primary"
              onClick={() => setCvcDialogOpen(true)}
            >
              ※セキュリティコード（CVC）について
            </Button>

            <Tooltip 
              title="クレジットカード会社が発行する利用明細書を領収書としてご利用ください"
              placement="top"
            >
              <Button color="primary">
                経費精算について
              </Button>
            </Tooltip>
          </Box>

          {/* カード要素とボタンを中央揃えに */}
          <Box display="flex" flexDirection="column" alignItems="center" my={3}>
            <div 
              id="card-element" 
              style={{
                width: '300px',
                padding: '10px',
                border: '1px solid gray',
                borderRadius: '3px',
                marginBottom: '10px'
              }}
            />
            <div 
              id="card-errors" 
              role="alert"
              style={{ 
                color: 'red',
                marginTop: '8px',
                minHeight: '20px',
                width: '300px',
                textAlign: 'center'
              }} 
            />
            <Button
              variant="contained"
              style={{ 
                backgroundColor: '#ffc107',
                color: 'rgba(0, 0, 0, 0.87)',
                marginTop: '16px'
              }}
              onClick={handlePayment}
              disabled={!checkPrivacyPolicy || !checkProvideInfo || showStopFlag}
            >
              クレジットカードで購入
            </Button>
          </Box>
        </Box>
      )}

      {/* CVCダイアログ */}
      <Dialog
        open={cvcDialogOpen}
        onClose={() => setCvcDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          セキュリティコード(CVC)の入力について
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            クレジットカード決済時には、セキュリティコード(CVC)の入力が必要になります。
          </Typography>
          <Typography variant="body2" paragraph>
            セキュリティコード(CVC)の下記のようにカードに記載されています。お持ちのクレジットカードをご確認ください。
          </Typography>
          <Box textAlign="center">
            <img 
              src="/image/stripe_manual/cvc_figure.png" 
              alt="CVCの位置" 
              style={{ 
                maxWidth: '400px',
                width: '90%'
              }} 
            />
          </Box>
        </DialogContent>
      </Dialog>

      {showThxPage && (
        <Box textAlign="center">
          <Typography variant="body2" gutterBottom>
            購入者ID: {userID}
          </Typography>
          <Typography variant="body2">
            ご利用ありがとうございました。
          </Typography>
        </Box>
      )}

      {showStopFlag && (
        <Box textAlign="center">
          <Typography variant="h6" gutterBottom>
            現在この商品は購入できません。
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default PurchaseProductWrapper;  // PurchaseProductWrapperをエクスポーネント
